import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  TopToolbar,
  EditButton,
  ListButton,
  useRecordContext,
  useRedirect,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Chip,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Factory as FactoryIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';
import { dataProvider } from '@/providers/dataProvider';

// Componente para mostrar las zonas de la planta
const ZonesListSection = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (record?.id) {
      const fetchZones = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await dataProvider.getList('zones', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'zoneName', order: 'ASC' },
            filter: { plantId: record.id },
          });
          setZones(response.data);
        } catch (err) {
          setError('Error al cargar las zonas');
          console.error('Error fetching zones:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchZones();
    }
  }, [record?.id]);

  if (!record) return null;

  return (
    <Card
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        backgroundColor: 'background.default',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocationIcon color='primary' />
          <Typography variant='h6' fontWeight='bold'>
            Zonas de esta Planta
          </Typography>
          <Chip
            label={zones.length}
            size='small'
            color='primary'
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : zones.length > 0 ? (
          <Paper
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <List dense>
              {zones.map((zone: any) => (
                <ListItem
                  key={zone.id}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    <IconButton
                      edge='end'
                      onClick={() => redirect(`/zones/${zone.id}/show`)}
                      size='small'
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <LocationIcon color='primary' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant='body2' fontWeight='medium'>
                        {zone.zoneName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant='caption' color='text.secondary'>
                        ID: {zone.id}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 2, textAlign: 'center' }}
          >
            No hay zonas asociadas a esta planta
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Componente para mostrar información del cliente
const ClientInfoSection = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Card
      sx={{
        height: 300,
        boxShadow: 3,
        borderRadius: 2,
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BusinessIcon color='primary' />
          <Typography variant='h6' fontWeight='bold'>
            Cliente Asociado
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <ReferenceField source='clientId' reference='clients' link='show'>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <TextField
                source='clientName'
                variant='h6'
                sx={{ fontWeight: 'medium' }}
              />
              <Typography variant='body2' color='text.secondary'>
                Cliente ID: {record.clientId}
              </Typography>
            </Box>
          </Box>
        </ReferenceField>
      </CardContent>
    </Card>
  );
};

// Componente para mostrar información de contacto
const ContactInfoSection = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Card
      sx={{
        height: 300,
        boxShadow: 3,
        borderRadius: 2,
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PhoneIcon color='primary' />
          <Typography variant='h6' fontWeight='bold'>
            Información de Contacto
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {record.clientPhoneContact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color='action' fontSize='small' />
              <Box>
                <Typography variant='body2' color='text.secondary'>
                  Teléfono
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {record.clientPhoneContact}
                </Typography>
              </Box>
            </Box>
          )}

          {record.clientEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color='action' fontSize='small' />
              <Box>
                <Typography variant='body2' color='text.secondary'>
                  Email
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {record.clientEmail}
                </Typography>
              </Box>
            </Box>
          )}

          {!record.clientPhoneContact && !record.clientEmail && (
            <Typography
              variant='body2'
              color='text.secondary'
              fontStyle='italic'
            >
              No hay información de contacto disponible
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Componente para mostrar roles y permisos asignados
const RolePermissionsSection = () => {
  const record = useRecordContext();
  if (!record) return null;

  const rolePermissions = record.rolePermissions || [];

  return (
    <Card
      sx={{
        height: 300,
        boxShadow: 3,
        borderRadius: 2,
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GroupIcon color='primary' />
          <Typography variant='h6' fontWeight='bold'>
            Roles y Permisos
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {rolePermissions.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rolePermissions.map((rp: any, index: number) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
              >
                <Chip
                  label={rp.role}
                  variant='outlined'
                  color='primary'
                  size='small'
                  sx={{
                    fontWeight: 'medium',
                  }}
                />
                <Chip
                  label={rp.permissionLevel}
                  variant='filled'
                  color='secondary'
                  size='small'
                  sx={{
                    fontWeight: 'medium',
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary' fontStyle='italic'>
            No hay roles asignados a esta planta
          </Typography>
        )}

        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mt: 2, display: 'block' }}
        >
          Total de roles: {rolePermissions.length}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Toolbar personalizada
const ShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteWithPasswordButton resource='plants' label='Eliminar' />
    <ListButton />
  </TopToolbar>
);

// Componente de título personalizado
const ShowTitle = () => {
  const record = useRecordContext();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
        <FactoryIcon sx={{ fontSize: 28 }} />
      </Avatar>
      <Box>
        <Typography variant='h4' component='h1' fontWeight='bold'>
          {record?.plantName || 'Cargando...'}
        </Typography>
        <Typography variant='subtitle1' color='text.secondary'>
          Planta ID: {record?.id || 'N/A'}
        </Typography>
      </Box>
    </Box>
  );
};

const PlantsShow = () => {
  return (
    <Show
      actions={<ShowActions />}
      sx={{
        '& .RaShow-card': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box sx={{ padding: 3, margin: '0 auto' }}>
        <ShowTitle />

        <Grid container spacing={3}>
          {' '}
          {/* Información Principal */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: 300,
                boxShadow: 3,
                borderRadius: 2,
                transition: 'box-shadow 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <InfoIcon color='primary' />
                  <Typography variant='h6' fontWeight='bold'>
                    Información General
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <SimpleShowLayout sx={{ backgroundColor: 'transparent' }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant='body2' color='text.secondary'>
                        ID de la Planta
                      </Typography>
                      <TextField
                        source='id'
                        sx={{
                          '& .MuiTypography-root': {
                            fontWeight: 'medium',
                            fontSize: '1.1rem',
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant='body2' color='text.secondary'>
                        Nombre de la Planta
                      </Typography>
                      <TextField
                        source='plantName'
                        sx={{
                          '& .MuiTypography-root': {
                            fontWeight: 'medium',
                            fontSize: '1.1rem',
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                </SimpleShowLayout>
              </CardContent>
            </Card>
          </Grid>
          {/* Cliente Asociado */}
          <Grid item xs={12} md={6}>
            <ClientInfoSection />
          </Grid>
          {/* Información de Contacto */}
          <Grid item xs={12} md={6}>
            <ContactInfoSection />
          </Grid>
          {/* Roles Asignados */}
          <Grid item xs={12} md={6}>
            <RolePermissionsSection />
          </Grid>
          {/* Zonas de la Planta */}
          <Grid item xs={12}>
            <ZonesListSection />
          </Grid>
        </Grid>
      </Box>
    </Show>
  );
};

export default PlantsShow;
